import { Cross1Icon } from "@radix-ui/react-icons";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { use } from "react";

import {
  BlockBodyBase,
  BlockContainerBase,
  BlockHeader,
  ColorVariant,
  theme,
} from "@/components/canvas-editor/components/Container";
import { ReadonlyEditorContext } from "@/components/canvas-editor/utils";
import {
  CanvasRegexTextArea,
  CanvasRegexTextAreaProps,
} from "@/components/CanvasRegexTextArea";
import { ReadOnlyValue } from "@/components/hooks/ListContainer";
import { Button, ButtonProps } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input, InputProps } from "@/components/ui/input";
import { SimpleSelect, SimpleSelectProps } from "@/components/ui/select";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface SimpleNodeBaseProps {
  type?: "include" | "exclude";
  variant?: ColorVariant;
  onRemove?: () => void;
  onMoveUp?: (() => void) | undefined | false;
  onMoveDown?: (() => void) | undefined | false;
  className?: string;
}

export function SimpleNode({
  type,
  variant,
  title,
  onRemove,
  children,
  onMoveUp,
  onMoveDown,
  showMustInclude,
  mustInclude,
  onMustIncludeChange,
  isReadonly: isReadonlyProp,
  className,
}: SimpleNodeBaseProps & {
  title: string;
  children: React.ReactNode;
  showMustInclude?: boolean;
  mustInclude?: boolean;
  onMustIncludeChange?: (mustInclude: boolean) => void;
  isReadonly?: boolean;
}) {
  const isReadonly = use(ReadonlyEditorContext) || isReadonlyProp;
  const colorVariant = variant ?? (type === "include" ? "green" : "red");

  return (
    <BlockContainerBase asDiv variant={colorVariant} className={className}>
      <BlockHeader>
        <div className="flex-1">{title}</div>

        {showMustInclude && !isReadonly && (
          <SimpleTooltip
            message={
              <div className="flex flex-col gap-1">
                <div>
                  The post <b>must </b> match this filter.
                </div>
                <div>
                  By default if a post matches any of the filters it will be
                  included in the feed.
                </div>
                <div>
                  This setting will make it so this filter is required in
                  addition to any of the other filters.e
                </div>
              </div>
            }
          >
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="flex items-center space-x-2">
              <Checkbox
                checked={mustInclude}
                onCheckedChange={onMustIncludeChange}
              />
              <div className="text-xs font-medium leading-none text-slate-dim">
                Must Include
              </div>
            </label>
          </SimpleTooltip>
        )}

        {isReadonly && showMustInclude && (
          <div
            className={cn(
              "flex gap-2 px-1.5 py-1 rounded-md",
              theme.surface[type === "include" ? "green" : "red"],
            )}
          >
            <div
              className={cn(
                "text-xs font-medium leading-none",
                theme.text[type === "include" ? "green" : "red"],
              )}
            >
              Must Include
            </div>
          </div>
        )}

        {!isReadonly && (
          <div className="flex gap-2">
            {onMoveUp !== false && (
              <Button
                size="xsIcon"
                variant="ghost"
                onClick={onMoveUp}
                disabled={!onMoveUp}
              >
                <span className="sr-only">Move up</span>
                <ArrowUpIcon />
              </Button>
            )}
            {onMoveDown !== false && (
              <Button
                size="xsIcon"
                variant="ghost"
                onClick={onMoveDown}
                disabled={!onMoveDown}
              >
                <span className="sr-only">Move down</span>
                <ArrowDownIcon />
              </Button>
            )}
            <Button size="xsIcon" variant="ghost" onClick={onRemove}>
              <span className="sr-only">Remove</span>
              <Cross1Icon />
            </Button>
          </div>
        )}
      </BlockHeader>
      <BlockBodyBase>
        <div className="flex flex-col gap-2 w-full" data-node>
          {children}
        </div>
      </BlockBodyBase>
    </BlockContainerBase>
  );
}

export function SimpleNodeButton(props: ButtonProps) {
  const isReadonly = use(ReadonlyEditorContext);

  if (isReadonly) {
    return null;
  }

  return <Button {...props} />;
}

export function SimpleNodeInput(props: InputProps) {
  const isReadonly = use(ReadonlyEditorContext);

  if (isReadonly) {
    return <ReadOnlyValue>{props.value}</ReadOnlyValue>;
  }

  return <Input {...props} />;
}

export function SimpleNodeRemoveableInput({
  onRemove,
  ...props
}: InputProps & { onRemove?: () => void }) {
  const isReadonly = use(ReadonlyEditorContext);

  return (
    <div className="flex items-center gap-2 relative group">
      <SimpleNodeInput {...props} />

      {!isReadonly && onRemove && (
        <Button
          size="xsIcon"
          variant="ghost"
          onClick={onRemove}
          className="absolute right-1.5 top-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <span className="sr-only">Remove</span>
          <Cross1Icon />
        </Button>
      )}
    </div>
  );
}

function SimpleNodeRegexTextArea(props: CanvasRegexTextAreaProps) {
  const isReadonly = use(ReadonlyEditorContext);

  if (isReadonly) {
    return <ReadOnlyValue>{props.value}</ReadOnlyValue>;
  }

  return <CanvasRegexTextArea {...props} />;
}

export function SimpleNodeRemoveableRegexTextArea({
  onRemove,
  ...props
}: CanvasRegexTextAreaProps & { onRemove?: () => void }) {
  const isReadonly = use(ReadonlyEditorContext);

  return (
    <div className="flex items-center gap-2 relative group">
      <SimpleNodeRegexTextArea {...props} />

      {!isReadonly && onRemove && (
        <Button
          size="xsIcon"
          variant="ghost"
          onClick={onRemove}
          className="absolute right-1.5 top-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <span className="sr-only">Remove</span>
          <Cross1Icon />
        </Button>
      )}
    </div>
  );
}

export function SimpleNodeSimpleSelect(props: SimpleSelectProps) {
  const isReadonly = use(ReadonlyEditorContext);

  if (isReadonly) {
    return <ReadOnlyValue>{props.value}</ReadOnlyValue>;
  }

  return <SimpleSelect {...props} />;
}
